import type { CheckboxProps } from '@mui/material/Checkbox';
import FormControl, { type FormControlProps } from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import type { SxProps, Theme } from '@mui/material/styles';
import isEqual from 'lodash/isEqual';
import { type ComponentType, type JSX, type ReactNode, useCallback, useMemo } from 'react';
import { type Control, type FieldPath, type FieldValues, useController } from 'react-hook-form';
import { Checkbox } from '../Checkbox/Checkbox.tsx';
import type { AppearanceProps } from '../Checkbox/CheckboxAppearance.tsx';

export type MuiCheckboxProps = CheckboxProps & { 'data-test'?: string };
type Value = boolean | string | number | null | Record<string, unknown>;

export type RHFMuiCheckboxProps<T extends FieldValues> = Omit<MuiCheckboxProps, 'name'> & {
  control?: Control<T>;
  disabled?: boolean;
  error?: boolean;
  formControlLabelSx?: SxProps<Theme>;
  fullWidth?: boolean;
  helperText?: string;
  label?: ReactNode;
  margin?: FormControlProps['margin'];
  name: FieldPath<T>;
  valueOfChecked?: Value;
  valueOfUnchecked?: Value;
  isChecked?: (value: Value) => boolean;
  Appearance?: ComponentType<AppearanceProps>;
};

export function RHFCheckboxField<T extends Record<string, unknown> = Record<string, boolean>>({
  control,
  error,
  formControlLabelSx,
  name,
  fullWidth,
  helperText,
  label,
  margin,
  'data-test': dataTest,
  valueOfUnchecked = false,
  valueOfChecked = true,
  isChecked,
  ...rest
}: RHFMuiCheckboxProps<T>): JSX.Element {
  const {
    field,
    fieldState,
    formState: { isSubmitting },
  } = useController({
    control,
    name,
  });

  const handleChange = useCallback<Required<CheckboxProps>['onChange']>(
    (event, value) => {
      rest.onChange?.(event, value);

      if (event.isDefaultPrevented()) {
        return;
      }
      const nextValueOfChecked =
        valueOfChecked && typeof valueOfChecked === 'object' ? structuredClone(valueOfChecked) : valueOfChecked;
      const nextValueOfUnchecked =
        valueOfUnchecked && typeof valueOfUnchecked === 'object' ? structuredClone(valueOfUnchecked) : valueOfUnchecked;

      field.onChange(value ? nextValueOfChecked : nextValueOfUnchecked);
    },
    [rest.onChange, field.onChange, valueOfChecked, valueOfUnchecked]
  );

  const hasError = fieldState.invalid || error;
  const helperTextOrErrorMsg = fieldState.error?.message || helperText;

  const checked = useMemo(() => {
    if (isChecked) {
      return isChecked(field.value as Value);
    }

    return isEqual(field.value, valueOfChecked);
  }, [isChecked, field.value, valueOfChecked]);

  const { ref, ...rhfField } = field;

  return (
    <FormControl data-test={dataTest} fullWidth={fullWidth} margin={margin} error={hasError}>
      <FormControlLabel
        ref={ref}
        control={
          <Checkbox
            {...rhfField}
            {...rest}
            checked={checked}
            disabled={isSubmitting || rest.disabled}
            isError={hasError}
            onChange={handleChange}
            data-test={dataTest ? `${dataTest}-checkbox` : undefined}
          />
        }
        label={label}
        sx={formControlLabelSx}
      />
      {helperTextOrErrorMsg && <FormHelperText error={hasError}>{helperTextOrErrorMsg}</FormHelperText>}
    </FormControl>
  );
}
